'use client';

import { useCallback, useRef } from 'react';
import { useCuratorSnackbar } from './useCuratorSnackbar';

interface CreateFileSizeLimitHandlerOptions {
  maxSizeInMB: number;
  onValid: (file: File | null, event: React.ChangeEvent<HTMLInputElement>) => void;
  onInvalid?: (message: string, file: File, event: React.ChangeEvent<HTMLInputElement>) => void;
  message?: string;
}

const readFileAsDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result);
        return;
      }
      reject(new Error('FileReader result is not a string'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });

const dataUrlToFile = async (dataUrl: string, original: File): Promise<File> => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], original.name, {
    type: original.type || blob.type,
    lastModified: original.lastModified,
  });
};

export const useFileSizeLimit = () => {
  const snackbar = useCuratorSnackbar();
  const lastErrorIdRef = useRef<string | null>(null);

  return useCallback(
    ({ maxSizeInMB, onValid, onInvalid, message }: CreateFileSizeLimitHandlerOptions) => {
      const maxBytes = maxSizeInMB * 1024 * 1024;
      const defaultMessage = `파일 크기는 ${maxSizeInMB}MB를 초과할 수 없습니다.`;

      return async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null;
        const displayMessage = message ?? defaultMessage;

        if (!file) {
          onValid(null, event);
          return;
        }

        if (file.size > maxBytes) {
          onInvalid?.(displayMessage, file, event);
          if (lastErrorIdRef.current) {
            snackbar.close(lastErrorIdRef.current);
          }
          lastErrorIdRef.current = snackbar.error(displayMessage);

          const input = event.target;
          input.value = '';
          requestAnimationFrame(() => {
            input.value = '';
          });
          return;
        }

        // NOTE: Android WebView에서 content:// URI 만료로 인한 ERR_UPLOAD_FILE_CHANGED를
        // 회피하기 위해 선택 직후 FileReader로 바이트를 읽어 인메모리 File로 치환한다.
        // 29CM 리뷰쓰기의 readAsDataURL 패턴을 참고.
        let snapshot: File = file;
        try {
          const dataUrl = await readFileAsDataURL(file);
          snapshot = await dataUrlToFile(dataUrl, file);
        } catch (error) {
          console.warn('[useFileSizeLimit] FileReader snapshot failed, falling back to raw File', {
            name: file.name,
            size: file.size,
            error,
          });
        }

        onValid(snapshot, event);
      };
    },
    [snackbar],
  );
};
